import {
  type ReviewResult,
  dayMonth,
  todayIso,
  useReviewAnswer,
  useReviewSummary,
  useReviewToday,
} from '@shkola/api';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GRAD, INK, INK_FAINT, INK_SOFT, PAPER, PillIcon, grad, pillClass } from '../design/forms';
import { ArrowUpRightIcon, CaretRightIcon, CheckIcon } from '../design/icons';
import { plain } from './home/model';

/*
 * «Повторення» in «Форми 2» (28.10): a white block, the title light with the way into «Дерево знань» as a
 * pill with ↗; the answers as pills — the chosen one dark, after the check the right ones mint with a
 * tick and a wrong pick struck; the dark pill to check and to go on.
 */

/**
 * «Повторення»: three questions of earlier tests a day — one by one, the right answer after each,
 * and when the question comes back.
 */
export function ReviewCard({
  studentId,
  className = '',
}: {
  studentId: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const today = todayIso();
  const questions = useReviewToday();
  const summary = useReviewSummary(studentId, today);
  const answer = useReviewAnswer();
  const [index, setIndex] = useState(0);
  const [chosen, setChosen] = useState<number[]>([]);
  const [result, setResult] = useState<ReviewResult>();
  const list = questions.data ?? [];
  const q = list[index];
  const finished = list.length > 0 && index >= list.length;

  const head = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2
        className="font-display text-[30px] leading-[36px] font-light tracking-[-0.03em]"
        style={{ color: INK }}
      >
        {t('review.title')}
      </h2>
      <Link to="/tree" className={pillClass('light', 'sm')}>
        {t('review.tree')}
        <PillIcon icon={ArrowUpRightIcon} size="sm" />
      </Link>
    </div>
  );

  if (questions.isPending) return null;
  if (!q) {
    const s = summary.data;
    return (
      <section className={`flex flex-col gap-3 rounded-[28px] bg-white p-5 md:p-6 ${className}`}>
        {head}
        <p className="text-[17px]" style={{ color: INK }}>
          {finished ? t('review.done') : s && s.total > 0 ? t('review.nothing') : t('review.empty')}
        </p>
        {s && s.total > 0 && (
          <p className="text-[14px]" style={{ color: INK_SOFT }}>
            {t('review.stats', { remembered: s.remembered, total: s.total })}
            {s.nextOn && ` · ${t('review.nextOn', { date: dayMonth(s.nextOn) })}`}
          </p>
        )}
      </section>
    );
  }

  const toggle = (i: number) =>
    setChosen(
      q.multiple ? (chosen.includes(i) ? chosen.filter((x) => x !== i) : [...chosen, i]) : [i],
    );
  return (
    <section className={`flex flex-col gap-4 rounded-[28px] bg-white p-5 md:p-6 ${className}`}>
      {head}
      <p className="-mt-2 text-[14px]" style={{ color: INK_SOFT }}>
        {t('review.intro')} · {t('review.of', { n: index + 1, total: list.length })}
      </p>
      <div>
        <p className="text-[12.5px] font-medium" style={{ color: INK_FAINT }}>
          {q.subject}
          {q.topic && ` · ${q.topic}`}
        </p>
        <p className="mt-1 text-[18px] leading-[26px]" style={{ color: INK }}>
          {q.text}
        </p>
        {q.multiple && (
          <p className="mt-1 text-[13px]" style={{ color: INK_SOFT }}>
            {t('review.pickMany')}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {q.options.map((option, i) => {
          const picked = chosen.includes(i);
          const right = result?.right.includes(i);
          return (
            <button
              key={`${i}-${option}`}
              type="button"
              disabled={result !== undefined}
              aria-pressed={picked}
              onClick={() => toggle(i)}
              className={`inline-flex h-11 items-center gap-1.5 rounded-full px-5 text-[15px] font-medium transition-colors ${result && !right && picked ? 'line-through' : ''} ${result || picked ? '' : 'hover:bg-[#EFECE5]'}`}
              style={
                result
                  ? right
                    ? { backgroundImage: grad('mint'), color: GRAD.mint.ink }
                    : { backgroundColor: PAPER, color: picked ? INK_FAINT : INK_SOFT }
                  : picked
                    ? { backgroundColor: 'var(--sh-dark)', color: '#FFFFFF' }
                    : { backgroundColor: PAPER, color: INK }
              }
            >
              {result && right && <CheckIcon size={14} weight="bold" aria-hidden />}
              {option}
            </button>
          );
        })}
      </div>
      {result ? (
        <div className="motion-rise flex flex-wrap items-center justify-between gap-3">
          <div className="text-[15px]">
            <p className="font-medium" style={{ color: INK }}>
              {plain(result.correct ? t('review.right') : t('review.wrong'))}
            </p>
            <p className="text-[14px]" style={{ color: INK_SOFT }}>
              {t('review.later', { date: dayMonth(result.nextOn) })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setIndex(index + 1);
              setChosen([]);
              setResult(undefined);
            }}
            className={pillClass('dark')}
          >
            {index + 1 < list.length ? t('review.next') : t('review.finish')}
            <PillIcon icon={index + 1 < list.length ? CaretRightIcon : CheckIcon} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={chosen.length === 0 || answer.isPending}
          onClick={() => void answer.mutateAsync({ questionId: q.id, chosen }).then(setResult)}
          className={`${pillClass('dark')} self-start disabled:opacity-35`}
        >
          {t('review.check')}
          <PillIcon icon={CheckIcon} />
        </button>
      )}
    </section>
  );
}
